import { useCallback, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { endOfMonth, format, parseISO } from "date-fns";

import {
  defaultFilters,
  type TransactionFilters,
} from "@/components/transactions/TransactionFilters";

/**
 * URL state for the AI Analysis tab's transaction list (the list corrections edit), so a link
 * ("Show in application") can open it filtered, with rows highlighted. On /applications/:id:
 *
 *   ?tab=ai-analysis            the page tab (both detail pages read any of their tab keys)
 *   &run=42                     the analysis run the link was made from
 *   &month=2026-09              filter: that month's credits (as a Recent revenue month click)
 *   &keys=<k>&keys=<k>          filter: only these rows, by override key
 *   &position=<key>             filter: the position's transactions, by its override key
 *   &highlight=<k>&highlight=<k>  rows to emphasize, by override key; the first scrolls into view
 *
 * One filter at most; a URL with several uses the first of keys, position, month. Keys repeat
 * rather than join, since override keys hold `|` and `:`. A URL with none of these is no link,
 * and the list behaves as it does without one.
 */
export const TAB_PARAM = "tab";
export const RUN_PARAM = "run";
export const MONTH_PARAM = "month";
export const KEYS_PARAM = "keys";
export const POSITION_PARAM = "position";
export const HIGHLIGHT_PARAM = "highlight";

/** The page tab that holds the list. */
export const ANALYSIS_TAB = "ai-analysis";

const LIST_PARAMS = [RUN_PARAM, MONTH_PARAM, KEYS_PARAM, POSITION_PARAM, HIGHLIGHT_PARAM];
const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

/** What the list shows. */
export type AnalysisListFilter =
  /** "YYYY-MM": credits from the 1st to the month's end. */
  | { kind: "month"; month: string }
  /** Only these rows, by override key (e.g. a proposal's transactions). */
  | { kind: "keys"; keys: string[] }
  /** The position's transactions, by the position's override key. */
  | { kind: "position"; position: string };

export interface AnalysisListLink {
  /** The run the link was made from; the tab shows the latest run, and says so if they differ. */
  run: number | null;
  filter: AnalysisListFilter | null;
  /** Override keys of the rows to emphasize. */
  highlight: string[];
}

/** Distinct non-empty values, in order. */
function values(params: URLSearchParams, name: string): string[] {
  return [...new Set(params.getAll(name).filter(Boolean))];
}

/** The list's link state in a query string, or null when it carries none. */
export function parseAnalysisListLink(params: URLSearchParams): AnalysisListLink | null {
  const runText = params.get(RUN_PARAM) ?? "";
  const run = /^[1-9]\d*$/.test(runText) ? Number(runText) : null;
  const keys = values(params, KEYS_PARAM);
  const position = params.get(POSITION_PARAM) || "";
  const month = params.get(MONTH_PARAM) ?? "";
  const filter: AnalysisListFilter | null =
    keys.length > 0
      ? { kind: "keys", keys }
      : position
        ? { kind: "position", position }
        : MONTH.test(month)
          ? { kind: "month", month }
          : null;
  const highlight = values(params, HIGHLIGHT_PARAM);
  if (run == null && filter == null && highlight.length === 0) return null;
  return { run, filter, highlight };
}

/** `params` without the list's link state (the tab is kept unless `withTab`). */
export function withoutAnalysisListLink(params: URLSearchParams, withTab = false): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const name of withTab ? [TAB_PARAM, ...LIST_PARAMS] : LIST_PARAMS) next.delete(name);
  return next;
}

/** Whether `params` carries any of the link's state, the tab included. */
export function hasAnalysisListLink(params: URLSearchParams): boolean {
  return [TAB_PARAM, ...LIST_PARAMS].some((name) => params.has(name));
}

/** The query string that opens the AI Analysis tab with `link`; other params in `base` are kept. */
export function analysisListSearch(
  link: Partial<AnalysisListLink>,
  base: URLSearchParams = new URLSearchParams()
): URLSearchParams {
  const params = withoutAnalysisListLink(base, true);
  params.set(TAB_PARAM, ANALYSIS_TAB);
  if (link.run != null) params.set(RUN_PARAM, String(link.run));
  const filter = link.filter;
  if (filter?.kind === "month") params.set(MONTH_PARAM, filter.month);
  if (filter?.kind === "position") params.set(POSITION_PARAM, filter.position);
  if (filter?.kind === "keys")
    for (const key of new Set(filter.keys)) params.append(KEYS_PARAM, key);
  for (const key of new Set(link.highlight ?? [])) params.append(HIGHLIGHT_PARAM, key);
  return params;
}

/** A page tab named in the query string, when it is one of the page's `keys`. */
export function linkedTab<K extends string>(params: URLSearchParams, keys: readonly K[]): K | null {
  const tab = params.get(TAB_PARAM);
  return tab != null && (keys as readonly string[]).includes(tab) ? (tab as K) : null;
}

/** The list filters for a month's credits ("YYYY-MM"), the same as a Recent revenue month click. */
export function monthCreditsFilters(month: string): TransactionFilters {
  const dateFrom = `${month}-01`;
  return {
    ...defaultFilters,
    transaction_type: "credit",
    date_from: dateFrom,
    date_to: format(endOfMonth(parseISO(dateFrom)), "yyyy-MM-dd"),
  };
}

export interface AnalysisListLinkState {
  link: AnalysisListLink | null;
  /** Changes on every navigation, so the same link opened again applies (and scrolls) again. */
  key: string;
  /** Drops the list's params from the address (the tab stays), once the user changes the list. */
  leave: () => void;
}

/** The list's link state from the address, for the application page to hand to the tab. */
export function useAnalysisListLink(): AnalysisListLinkState {
  const [params, setParams] = useSearchParams();
  const { key } = useLocation();
  const link = useMemo(() => parseAnalysisListLink(params), [params]);
  const leave = useCallback(
    () =>
      setParams((current) => withoutAnalysisListLink(current), {
        replace: true,
        preventScrollReset: true,
      }),
    [setParams]
  );
  return { link, key, leave };
}
